import React, { useContext, useEffect, useRef, useState } from "react";
import {
    getDateFnsFormatDate,
    getWeekStartOrEndDate,
} from "shared/components/date-controls/week-navigator/WeekNavigatorMain";

import { DrawerContextProvider } from "shared/contexts/DrawerContext";
import Grid from "features/timesheet/timesheet-grid/Grid";
import Header from "features/timesheet/timesheet-grid/Header";
import LockedTimesheetFooter from "features/timesheet/timesheet-grid/LockedTimesheetFooter";
import SessionContext from "app/contexts/SessionContext";
import {
    StickyTimesheetHeader,
    TimesheetGridContainer,
} from "features/timesheet/timesheet-grid/TimesheetGrid.styles";
import SubmitTimesheet from "features/timesheet/timesheet-grid/SubmitTimesheet";
import { SubmitTimesheetContextProvider } from "features/timesheet/contexts/SubmitTimesheetContext";
import { TimesheetProjectRowExpansionContextProvider } from "features/timesheet/contexts/TimesheetProjectRowExpansionContext";
import WeekNavigatorContext from "shared/contexts/WeekNavigatorContext";
import { getCalendarDateFromNewDate } from "features/admin/offices/OfficesForm";
import { isBefore } from "date-fns";

const MemoizedHeader = React.memo(Header);
const MemoizedGrid = React.memo(Grid);

export const DAY_HOURS_LIMIT = 24;

export const checkTenureNotStarted = (selectedDate, tenureStartDate) => {
    const calendarWeekStartDateOfTenure = getWeekStartOrEndDate(
        0,
        true,
        getCalendarDateFromNewDate(new Date(tenureStartDate))
    );
    return isBefore(
        getDateFnsFormatDate(
            selectedDate.year,
            selectedDate.month,
            selectedDate.day
        ),
        getDateFnsFormatDate(
            calendarWeekStartDateOfTenure.year,
            calendarWeekStartDateOfTenure.month,
            calendarWeekStartDateOfTenure.day
        )
    );
};

const TimesheetGrid = () => {
    const session = useContext(SessionContext);

    if (!session.timesheetRequired) {
        globalThis.location.replace("/timesheets/");
    }

    const { selectedTimesheetStartDate } = useContext(WeekNavigatorContext);

    const tenureNotStarted = selectedTimesheetStartDate
        ? checkTenureNotStarted(selectedTimesheetStartDate, session.startDate)
        : true;

    const sentinelRef = useRef(null);
    const headerRef = useRef(null);
    const [isStuck, setIsStuck] = useState(false);
    const [unstuckHeaderHeight, setUnstuckHeaderHeight] = useState(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const stuck = !entry.isIntersecting;
                // The compact (stuck) header is shorter. Near the bottom of the page that
                // shrinks the document, the browser clamps the scroll position, the sentinel
                // comes back into view and the header un-sticks, looping forever (page "shakes").
                // Freezing the header at its un-stuck height keeps the document height stable.
                if (stuck && headerRef.current) {
                    setUnstuckHeaderHeight(headerRef.current.offsetHeight);
                }
                setIsStuck(stuck);
            },
            { rootMargin: "-50px 0px 0px 0px", threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    return (
        <TimesheetProjectRowExpansionContextProvider>
            <SubmitTimesheetContextProvider>
                <DrawerContextProvider>
                    <TimesheetGridContainer spacing="One">
                        <div ref={sentinelRef} />
                        <StickyTimesheetHeader
                            ref={headerRef}
                            $isStuck={isStuck}
                            style={
                                isStuck && unstuckHeaderHeight
                                    ? { minHeight: unstuckHeaderHeight }
                                    : undefined
                            }
                        >
                            <SubmitTimesheet isStuck={isStuck} />
                            <MemoizedHeader />
                        </StickyTimesheetHeader>
                        {!tenureNotStarted && <MemoizedGrid />}
                        {!tenureNotStarted && <LockedTimesheetFooter />}
                    </TimesheetGridContainer>
                </DrawerContextProvider>
            </SubmitTimesheetContextProvider>
        </TimesheetProjectRowExpansionContextProvider>
    );
};

export default TimesheetGrid;
